import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Grid from '@mui/material/Grid'
import Tooltip from '@mui/material/Tooltip'
import StopIcon from '@mui/icons-material/Stop'
import RefreshIcon from '@mui/icons-material/Refresh'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ReactPlayer from 'react-player'
import { toast } from 'react-toastify'

import StreamingManagementService from '../../services/StreamingManagementService'
import { ActiveChannel } from '../types'
import { GcpChannelStateBadge, StreamingStatusBadge } from './StatusBadge'
import { ForceStopModal } from './ActionModals'

const POLL_INTERVAL_MS = 30_000

export default function ActiveChannelsMonitor() {
  const [channels, setChannels] = useState<ActiveChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingGcp, setLoadingGcp] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forceStopTarget, setForceStopTarget] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchChannels = useCallback(async (checkGcp = false) => {
    try {
      const res = await StreamingManagementService.getActiveChannels(checkGcp)
      if (res?.data?.active_channels) {
        setChannels(res.data.active_channels)
        setError(null)
      }
    } catch {
      setError('Não foi possível carregar os canais ativos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchChannels()
    intervalRef.current = setInterval(() => fetchChannels(), POLL_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchChannels])

  const handleCheckGcp = async () => {
    setLoadingGcp(true)
    await fetchChannels(true)
    setLoadingGcp(false)
    toast.info('Estado GCP atualizado.')
  }

  const handleForceStopSuccess = () => {
    fetchChannels()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Canais Ativos{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            (atualiza a cada 30s)
          </Typography>
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Verificar estado real no GCP (mais lento)">
            <Button
              variant="outlined"
              size="small"
              startIcon={loadingGcp ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={handleCheckGcp}
              disabled={loadingGcp}
            >
              Verificar GCP
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchChannels()}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {channels.length === 0 && !error && (
        <Alert severity="info">Nenhum streaming ativo no momento.</Alert>
      )}

      <Grid container spacing={2}>
        {channels.map((ch) => (
          <Grid item xs={12} md={6} lg={4} key={ch.streaming_id}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {ch.camera_id} - {ch.event_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {ch.room_name} · {ch.camera_name}
                  {ch.main && ' (Principal)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {ch.entity_name}
                </Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <StreamingStatusBadge status={ch.status} />
                  <GcpChannelStateBadge state={ch.gcp_channel_state} />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Iniciado em: {new Date(ch.started_at).toLocaleString('pt-BR')}
                </Typography>
              </CardContent>
              <CardActions sx={{ pt: 0, flexWrap: 'wrap', gap: 1 }}>
                {ch.streaming_url && (
                  <Button
                    size="small"
                    startIcon={<PlayCircleOutlineIcon />}
                    onClick={() =>
                      setPreviewUrl(previewUrl === ch.streaming_url ? null : ch.streaming_url)
                    }
                  >
                    {previewUrl === ch.streaming_url ? 'Fechar player' : 'Assistir'}
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={() => setForceStopTarget(ch.streaming_id)}
                >
                  Forçar Parada
                </Button>
              </CardActions>
              {previewUrl === ch.streaming_url && ch.streaming_url && (
                <Box px={2} pb={2}>
                  <ReactPlayer
                    url={ch.streaming_url}
                    playing
                    controls
                    width="100%"
                    height="180px"
                    config={{ file: { forceHLS: true } }}
                  />
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <ForceStopModal
        open={!!forceStopTarget}
        streamingId={forceStopTarget}
        onClose={() => setForceStopTarget(null)}
        onSuccess={handleForceStopSuccess}
      />
    </Box>
  )
}
